// ClientRegistrationStep2.jsx (製品情報選択) - PWA対応版
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { 
  Container, 
  Typography, 
  Button, 
  Box, 
  Paper, 
  Card, 
  CardContent, 
  CircularProgress, 
  Alert,
  Grid,
} from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';

const ClientRegistrationStep2 = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [clientDetails, setClientDetails] = useState({
        id: "",
        name: ""
    });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const clientId = sessionStorage.getItem("registeredClientId");
        const clientName = sessionStorage.getItem("registeredClientName");
        const wasSuccess = sessionStorage.getItem("registrationSuccess");

        if (!clientId || !clientName || wasSuccess !== "true") {
            navigate("/client-registration-step1");
            return;
        }

        setClientDetails({
            id: clientId,
            name: clientName
        });

        // 画面表示後にサクセスメッセージを表示
        setSuccess(true);
    }, [navigate]);

    const handleAddProduct = () => {
        const clientId = sessionStorage.getItem("registeredClientId");
        
        // 新規登録からの遷移を示すフラグを設定
        sessionStorage.setItem("fromNewRegistration", "true");
        
        // 他のセッションストレージ項目をクリア
        sessionStorage.removeItem("registrationSuccess");
        sessionStorage.removeItem("registeredClientId");
        sessionStorage.removeItem("registeredClientName");

        // 顧客詳細ページへ遷移
        navigate(`/client/${clientId}`);
    };

    const handleSkipToHome = () => {
        // セッションストレージをクリア
        sessionStorage.removeItem("registrationSuccess");
        sessionStorage.removeItem("registeredClientId");
        sessionStorage.removeItem("registeredClientName");

        // ダッシュボードに戻る
        navigate("/dashboard");
    };

    return (
        <>
            <Header />
            <Container 
                maxWidth="md" 
                sx={{ 
                    mt: 4, 
                    mb: 6,
                    // PWAボトムナビゲーション対応：安全マージンを確保
                    pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
                    // 最小高さを確保してスクロール可能にする
                    minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' },
                    px: { xs: 2, sm: 3 }
                }}
            >
                {/* 成功メッセージ */}
                {success && (
                    <Alert 
                        icon={<CheckCircleIcon fontSize="inherit" />} 
                        severity="success" 
                        sx={{ mb: 3 }}
                    >
                        顧客情報の登録が完了しました
                    </Alert>
                )}

                {/* エラーメッセージ */}
                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ mb: 3 }}
                    >
                        {error}
                    </Alert>
                )}

                {/* 顧客情報カード */}
                <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Typography variant="h5" fontWeight="medium">
                            顧客情報
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mx: 2 }}>
                        <PersonIcon color="action" fontSize="small" />
                        <Typography variant="body1">
                            <strong>顧客名:</strong> {clientDetails.name}
                        </Typography>
                    </Box>
                </Paper>

                {/* 製品情報追加オプション */}
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <LocalGasStationIcon color="primary" />
                        <Typography variant="h5" fontWeight="medium">
                            製品情報の追加
                        </Typography>
                    </Box>

                    <Alert 
                        severity="info" 
                        icon={<InfoOutlinedIcon />}
                        sx={{ mb: 3 }}
                    >
                        製品情報（タンク容量、消費量など）を追加しますか？
                    </Alert>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    height: '100%',
                                    borderColor: 'primary.main',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3
                                    },
                                    cursor: 'pointer'
                                }}
                                onClick={handleAddProduct}
                            >
                                <CardContent sx={{ 
                                    textAlign: 'center', 
                                    p: 3,
                                    minHeight: { xs: 180, sm: 160 } // タッチしやすいサイズ
                                }}>
                                    <AddIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                                    <Typography variant="h6" gutterBottom>
                                        製品情報を追加する
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        顧客の詳細ページに移動して、製品情報を登録します
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    height: '100%',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3
                                    },
                                    cursor: 'pointer'
                                }}
                                onClick={handleSkipToHome}
                            >
                                <CardContent sx={{ 
                                    textAlign: 'center', 
                                    p: 3,
                                    minHeight: { xs: 180, sm: 160 } // タッチしやすいサイズ
                                }}>
                                    <ArrowForwardIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" gutterBottom>
                                        あとで追加する
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        製品情報の登録をスキップして、ダッシュボードに戻ります
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
        </>
    );
};

export default ClientRegistrationStep2;